import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI } from '../lib/api';
import { googleLogout } from '@react-oauth/google';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check for existing session on mount
  useEffect(() => {
    const initAuth = async () => {
      const token = localStorage.getItem('salty_token');
      const savedUser = localStorage.getItem('salty_user');
      
      if (token && savedUser) {
        try {
          // Verify token is still valid
          const response = await authAPI.getMe();
          setUser(response.data);
        } catch (err) {
          // Token invalid, clear storage
          localStorage.removeItem('salty_token');
          localStorage.removeItem('salty_user');
        }
      }
      setLoading(false);
    };

    initAuth();
  }, []);

  useEffect(() => {
    const handleUserUpdate = () => {
      const saved = localStorage.getItem('salty_user');
      if (saved) setUser(JSON.parse(saved));
    };
    window.addEventListener('salty_user_updated', handleUserUpdate);
    return () => window.removeEventListener('salty_user_updated', handleUserUpdate);
  }, []);

  const login = useCallback(async (email, password) => {
    setError(null);
    try {
      const response = await authAPI.login(email, password);
      const { access_token, user: userData } = response.data;
      
      localStorage.setItem('salty_token', access_token);
      localStorage.setItem('salty_user', JSON.stringify(userData));
      setUser(userData);
      
      return { success: true };
    } catch (err) {
      const message = err.response?.data?.detail || 'Login failed. Please try again.';
      setError(message);
      return { success: false, error: message };
    }
  }, []);

  const register = useCallback(async (data) => {
    setError(null);
    try {
      const response = await authAPI.register(data);
      const { access_token, user: userData } = response.data;
      
      localStorage.setItem('salty_token', access_token);
      localStorage.setItem('salty_user', JSON.stringify(userData));
      setUser(userData);
      
      return { success: true };
    } catch (err) {
      const message = err.response?.data?.detail || 'Registration failed. Please try again.';
      setError(message);
      return { success: false, error: message };
    }
  }, []);

  const loginWithGoogle = useCallback(async (credential) => {
    setError(null);
    try {
      const response = await authAPI.googleLogin(credential);
      const { access_token, user: userData } = response.data;
      localStorage.setItem('salty_token', access_token);
      localStorage.setItem('salty_user', JSON.stringify(userData));
      setUser(userData);
      return { success: true };
    } catch (err) {
      const message = err.response?.data?.detail || 'Google sign-in failed. Please try again.';
      setError(message);
      return { success: false, error: message };
    }
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('salty_token');
    localStorage.removeItem('salty_user');
    googleLogout();
    setUser(null);
  }, []);

  const value = {
    user,
    loading,
    error,
    loginWithGoogle,
    logout,
    isAuthenticated: !!user,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
